import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { pingServerRequest } from 'apps/builder/lib/appBuilderRequests'

import {
  addEntry,
  updateEntry,
  deleteEntry,
} from 'apps/builder/store/actions/entries'

import { actions as clientActions } from '@gocanvas/client'
const { toggleInteractiveClient } = clientActions

import { ActionTypes as UndoRedoActions } from 'redux-undo'

import {
  addSheet,
  addTableSheet,
  addListSheet,
  deleteSheet,
  deleteChildSheet,
  deleteLoopSheet,
  convertToLoopScreen,
  promoteToLoopScreen,
  convertToTableScreen,
  promoteToTableScreen,
} from 'apps/builder/store/actions/sheets'
import { updateOnSave } from 'apps/builder/store/actions/app'

import {
  fetchAppSettings,
  publishAppRequest,
} from 'apps/builder/lib/appBuilderRequests'

import { saveApp } from 'apps/formsCreationKit/store/reducers/settings/appSettings'

export const ping = createAsyncThunk(
  'settings/ping',
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await pingServerRequest(id)
      return data
    } catch (err) {
      return rejectWithValue(err.response.data)
    }
  }
)

import { updateOnPublish } from 'apps/builder/store/actions/app'
import { sortBy } from 'apps/builder/store/helpers'

export const fetchAppData = createAsyncThunk(
  'settings/getAppSettings',
  async (id, { rejectWithValue }) => {
    try {
      const response = await fetchAppSettings(id)
      return response.data
    } catch (err) {
      return rejectWithValue(err.response.data)
    }
  },
  {
    condition: (id, { getState }) => {
      const isLoading = getState().settings.isLoading
      if (isLoading) {
        return false
      }
    },
  }
)

export const publishApp = createAsyncThunk(
  'settings/publishApp',
  async (
    { appId, assignedUsers, assignedGroups, send_notification },
    { rejectWithValue }
  ) => {
    try {
      const response = await publishAppRequest(appId, {
        _method: 'patch',
        commit: 'Publish',
        form: {
          assigned_group_ids: assignedGroups,
          assigned_user_ids: assignedUsers,
        },
        send_notification,
      })
      return response.data
    } catch (err) {
      return rejectWithValue(err.response.data)
    }
  }
)

const clearActiveSheet = state => {
  return {
    ...state,
    activeSheet: '',
    activeLeftPanelTab: leftPanelTabs.LIBRARY,
  }
}

const handleAddTableSheet = (state, action) => {
  const {
    id,
    keyField: { guid: keyFieldGuid = '' },
  } = action.payload

  return {
    ...state,
    activeModal: '',
    activeSheet: id,
    activeEntry: keyFieldGuid,
  }
}

const handleConvertToTableOrLoopScreen = (state, action) => {
  const {
    newSheetId,
    keyField: { guid: keyFieldGuid = '' },
  } = action.payload
  return {
    ...state,
    activeModal: '',
    activeSheet: newSheetId,
    activeEntry: keyFieldGuid,
  }
}

const clearActiveEntitiesOnUndoRedo = (state, action) => {
  const { new_workspace = false } = action
  return !new_workspace
    ? state
    : {
        ...state,
        activeSheet: '',
        activeEntry: '',
        activeLeftPanelTab: leftPanelTabs.LIBRARY,
      }
}

export const initialSettingState = {
  activeEntry: '',
  activeScreen: '',
  activeSheet: '',
  fullScreenPreview: false,
  fullScreenWorkspace: false,
  showLeftPanel: true,
  activeLeftPanelTab: null,
  modalOpen: false,
  flashMessage: {},
  activePreview: '',
  unsavedChanges: false,
  appSettings: {},
  users: [],
  assignedUsers: [],
  groups: [],
  assignedGroups: [],
  loaded: false,
  isLoading: false,
  send_notification: true,
  isPublishPending: false,
  lastAction: null,
  reload_app: false,
  next_form_id: null,
  isSupport: false,
  readOnly: false,
  isAppSaving: false,
  is_form_publishable: null,
  is_web_form_publishable: null,
  num_of_published_forms: '',
  num_of_published_web_forms: '',
  folder_id: null,
  maxScreenNestingDepth: 4,
}

/**
 *
 * This checks the redux action against a permitted actions array.
 * If the action is in the permitted actions array, return true.
 * As a result, this will flip the unsavedChanges value to true.
 * Which is used by the save & publish buttons in builder
 */
const appChangeAction = action => {
  const ignoredActions = [
    'app/updateOnSave',
    'app/updateOnPublish',
    'app/updateBuilderVersion',
    'elements/selectElement',
    'pages/selectPage',
  ]

  if (ignoredActions.includes(action.type)) {
    return false
  }

  const allowedAppUpdateActionsStartWith = [
    'entries',
    'sections',
    'sheets',
    'screens',
    'conditions',
    'operations',
    'app',
    'referenceData',
    'liveData',
    'pdf',
    'elements',
    'pages',
  ]

  return allowedAppUpdateActionsStartWith.includes(action.type.split('/')[0])
}

const builderVersionActionLookup = action => {
  return ['app/updateBuilderVersion'].includes(action.type)
}

const storeLastAction = action => {
  const ignoredActions = [
    'settings/updateFlashMessage',
    'settings/ping/fulfilled',
    'settings/ping/pending',
    'settings/ping/rejected',
  ]
  return !ignoredActions.includes(action.type)
}

export const leftPanelTabs = {
  LIBRARY: 'Field Library',
  FIELD: 'Field Settings',
  SCREEN: 'Screen Settings',
}

export const settingsSliceExtraReducers = builder => {
  builder
    .addCase(toggleInteractiveClient, (state, action) => {
      return {
        ...state,
        fullScreenPreview: action.payload,
        activeEntry: '',
        activeLeftPanelTab: leftPanelTabs.LIBRARY,
      }
    })
    .addCase(updateEntry, (state, action) => {
      const { guid } = action.payload
      return {
        ...state,
        activeEntry: guid,
      }
    })
    .addCase(addEntry, (state, action) => {
      const { guid, source } = action.payload
      const { activeLeftPanelTab } = state
      return {
        ...state,
        activeEntry: source === 'quick-field' ? null : guid,
        // drag/dropped fields open field settings for the dropped field
        activeLeftPanelTab:
          source === 'drop' ? leftPanelTabs.FIELD : activeLeftPanelTab,
      }
    })
    .addCase(deleteEntry, (state, action) => {
      return {
        ...state,
        activeEntry: '',
        activeLeftPanelTab: leftPanelTabs.LIBRARY,
      }
    })
    .addCase(addSheet, (state, action) => {
      const { id } = action.payload
      return {
        ...state,
        activeSheet: id,
        activeLeftPanelTab: leftPanelTabs.SCREEN,
      }
    })
    // TODO: temporary fix for undo/redo, refector & remove
    .addCase(UndoRedoActions.UNDO, clearActiveEntitiesOnUndoRedo)
    .addCase(UndoRedoActions.REDO, clearActiveEntitiesOnUndoRedo)
    .addCase(convertToLoopScreen, handleConvertToTableOrLoopScreen)
    .addCase(promoteToLoopScreen, handleConvertToTableOrLoopScreen)
    .addCase(convertToTableScreen, handleConvertToTableOrLoopScreen)
    .addCase(promoteToTableScreen, handleConvertToTableOrLoopScreen)
    .addCase(deleteSheet, clearActiveSheet)
    .addCase(deleteChildSheet, clearActiveSheet)
    .addCase(deleteLoopSheet, clearActiveSheet)
    .addCase(addTableSheet, handleAddTableSheet)
    .addCase(addListSheet, handleAddTableSheet)
    .addCase(updateOnSave, (state, action) => {
      return {
        ...state,
        unsavedChanges: false,
      }
    })
    .addCase(fetchAppData.pending, (state, action) => {
      return {
        ...state,
        loaded: false,
        isLoading: true,
        activeModal: 'publish',
        activeEntry: '',
        activeLeftPanelTab: leftPanelTabs.LIBRARY,
      }
    })
    .addCase(fetchAppData.fulfilled, (state, action) => {
      const { users, groups, send_notification } = action.payload

      const filteredByAssigned = items =>
        items.filter(({ assigned }) => assigned).map(({ id }) => id)

      const sortByName = itemsToSort =>
        sortBy([...itemsToSort], 'asc', ({ name }) => name.toUpperCase())

      return {
        ...state,
        send_notification: send_notification,
        loaded: true,
        isLoading: false,
        users: sortByName(users),
        assignedUsers: filteredByAssigned(users),
        groups: sortByName(groups),
        assignedGroups: filteredByAssigned(groups),
      }
    })
    .addCase(fetchAppData.rejected, (state, action) => {
      return {
        ...state,
        loaded: false,
        isLoading: false,
      }
    })
    .addCase(saveApp.pending, (state, action) => {
      return {
        ...state,
        isAppSaving: true,
      }
    })
    .addCase(saveApp.fulfilled, (state, action) => {
      const { entities, settings } = action.payload
      const { sheets = [], entries = [] } = entities

      const activeEntry =
        entries.find(entry => `${entry.guid}` === `${state.activeEntry}`) || {}

      // After publishing, if the activeSheet was deleted, clear it from state
      const activeSheetStillExists = sheets.find(
        sheet =>
          `${sheet.id}` === `${activeEntry.sheet_id || state.activeSheet}`
      )

      const parentSheet =
        activeSheetStillExists &&
        sheets.find(
          ({ multi_section }) =>
            multi_section === activeSheetStillExists.section_id
        )

      const activeSheet = parentSheet || activeSheetStillExists

      return {
        ...state,
        isAppSaving: false,
        activeEntry: activeSheet ? activeEntry.guid : '',
        activeSheet: activeSheet ? `${activeSheet.id}` : '',
      }
    })
    .addCase(saveApp.rejected, (state, action) => {
      return { ...state, isAppSaving: false }
    })
    .addCase(publishApp.pending, (state, action) => {
      return {
        ...state,
        isPublishPending: true,
      }
    })
    .addCase(publishApp.fulfilled, (state, action) => {
      return {
        ...state,
        isPublishPending: false,
      }
    })
    .addCase(publishApp.rejected, (state, action) => {
      return {
        ...state,
        isPublishPending: false,
      }
    })
    .addCase(updateOnPublish, (state, action) => {
      return {
        ...state,
        loaded: false,
        activeModal: '',
      }
    })
    .addCase(ping.fulfilled, (state, action) => {
      const { payload } = action
      const {
        success,
        reload_app,
        form_id,
        is_form_publishable,
        is_web_form_publishable,
        num_of_published_forms,
        num_of_published_web_forms,
      } = payload
      const { activeModal, readOnly } = state

      return reload_app
        ? {
            ...state,
            readOnly: reload_app,
            next_form_id: form_id,
            activeModal:
              readOnly && activeModal !== 'reloadApp' ? '' : 'reloadApp',
            success: success,
          }
        : {
            ...state,
            is_form_publishable: is_form_publishable,
            is_web_form_publishable: is_web_form_publishable,
            num_of_published_forms: num_of_published_forms,
            num_of_published_web_forms: num_of_published_web_forms,
          }
    })
    .addMatcher(
      action => builderVersionActionLookup(action),
      (state, action) => {
        return {
          ...state,
          unsavedChanges: false,
        }
      }
    )
    .addMatcher(
      action => appChangeAction(action),
      (state, action) => {
        return { ...state, unsavedChanges: true }
      }
    )
    .addMatcher(
      action => storeLastAction(action),
      (state, action) => {
        return { ...state, lastAction: action }
      }
    )
}

export const settingsSliceConfig = {
  name: 'settings',
  initialState: initialSettingState,
  reducers: {
    resetWorkspaceFocus(state, action) {
      return {
        ...state,
        activeEntry: null,
        activeSheet: null,
        activeLeftPanelTab: leftPanelTabs.LIBRARY,
      }
    },
    selectSheet(state, action) {
      return {
        ...state,
        activeSheet: action.payload,
        activeEntry: null,
        activeLeftPanelTab: leftPanelTabs.SCREEN,
        hideSubmitButton: false,
      }
    },
    setActiveLeftPanelTab(state, action) {
      return {
        ...state,
        activeLeftPanelTab: action.payload,
      }
    },
    selectEntry(state, action) {
      const { activeEntry, activeSheet } = action.payload
      return {
        ...state,
        activeEntry: activeEntry,
        activeSheet: activeSheet || state.activeSheet,
        activeLeftPanelTab: leftPanelTabs.FIELD,
      }
    },
    selectScreen(state, action) {
      return {
        ...state,
        activeScreen: action.payload,
      }
    },
    toggleFullScreenWorkspace(state, action) {
      return {
        ...state,
        fullScreenWorkspace: action.payload,
      }
    },
    toggleLeftPanel(state, action) {
      return {
        ...state,
        showLeftPanel: action.payload,
      }
    },
    toggleModal(state, action) {
      return {
        ...state,
        ...action.payload,
        activePreview: action.payload.activePreview || '',
      }
    },
    togglePanel(state, action) {
      const { panelId, isOpen } = action.payload
      return {
        ...state,
        panelOverrides: {
          ...state.panelOverrides,
          [panelId]: isOpen,
        },
      }
    },
    updateFlashMessage(state, action) {
      const { message, type } = action.payload
      return {
        ...state,
        flashMessage: {
          message,
          type,
        },
      }
    },
    sortUsers: (state, action) => {
      return {
        ...state,
        users: sortBy([...state.users], action.payload.sortOrder, ({ name }) =>
          name.toUpperCase()
        ),
      }
    },
    sortGroups: (state, action) => {
      return {
        ...state,
        groups: sortBy(
          [...state.groups],
          action.payload.sortOrder,
          ({ name }) => name.toUpperCase()
        ),
      }
    },
    assignUser: (state, action) => {
      return {
        ...state,
        assignedUsers: [...state.assignedUsers, action.payload],
      }
    },
    assignGroup: (state, action) => {
      return {
        ...state,
        assignedGroups: [...state.assignedGroups, action.payload],
      }
    },
    assignAllUsers: (state, action) => {
      return {
        ...state,
        assignedUsers: state.users.map(({ id }) => id),
      }
    },
    assignAllGroups: (state, action) => {
      return {
        ...state,
        assignedGroups: state.groups.map(({ id }) => id),
      }
    },
    unassignUser: (state, action) => {
      return {
        ...state,
        assignedUsers: state.assignedUsers.filter(id => id != action.payload),
      }
    },
    unassignGroup: (state, action) => {
      return {
        ...state,
        assignedGroups: state.assignedGroups.filter(id => id != action.payload),
      }
    },
    unassignAllUsers: (state, action) => {
      return {
        ...state,
        assignedUsers: [],
      }
    },
    unassignAllGroups: (state, action) => {
      return {
        ...state,
        assignedGroups: [],
      }
    },
    updateNotification: (state, action) => {
      return { ...state, send_notification: action.payload }
    },
    saveAndPublishLoaded: (state, action) => {
      return {
        ...state,
        loaded: action.payload,
      }
    },
    updateUnsavedChanges: (state, action) => {
      return {
        ...state,
        unsavedChanges: action.payload,
      }
    },
  },
  extraReducers: settingsSliceExtraReducers,
}

export const settingsSlice = createSlice(settingsSliceConfig)

export const { actions, reducer } = settingsSlice

export const appSettingsReducer = reducer
